import { reactive } from 'vue'

const nativeFetch = window.fetch.bind(window)

export const adminSecurity = reactive({
  apiBaseUrl: '',
  authenticated: false,
  user: null,
  csrfToken: '',
  challenge: null,
  recoveryCodes: [],
  loading: false,
})

const apiUrl = (endpoint) => `${adminSecurity.apiBaseUrl}/API/${endpoint}`

const readPayload = async (response) => {
  const data = await response.json().catch(() => ({ success: false, message: `HTTP ${response.status}` }))
  if (!response.ok && !data.message) data.message = `HTTP ${response.status}`
  return data
}

const acceptAuth = (data) => {
  adminSecurity.authenticated = Boolean(data.authenticated)
  adminSecurity.user = data.user || null
  adminSecurity.csrfToken = data.csrfToken || ''
  if (adminSecurity.authenticated) adminSecurity.challenge = null
  if (Array.isArray(data.recoveryCodes)) adminSecurity.recoveryCodes = data.recoveryCodes
  return data
}

export const configureAdminSecurity = (apiBaseUrl = '') => {
  adminSecurity.apiBaseUrl = String(apiBaseUrl || '').replace(/\/$/, '')
  if (window.__secureAdminFetchInstalled) return
  window.__secureAdminFetchInstalled = true
  window.fetch = async (input, init = {}) => {
    const requestUrl = typeof input === 'string' ? input : input.url
    if (!requestUrl.includes('/API/')) return nativeFetch(input, init)
    const options = { ...init }
    const method = String(options.method || (typeof input !== 'string' ? input.method : 'GET')).toUpperCase()
    const headers = new Headers(options.headers || (typeof input !== 'string' ? input.headers : undefined))
    if (method !== 'GET' && method !== 'HEAD' && adminSecurity.csrfToken) {
      headers.set('X-CSRF-Token', adminSecurity.csrfToken)
    }

    if (requestUrl.includes('/API/saveJson.php') && typeof options.body === 'string') {
      try {
        const body = JSON.parse(options.body)
        if (body.filename && !body.expectedSha256) {
          const current = await nativeFetch(apiUrl('readJson.php'), {
            method: 'POST',
            body: new URLSearchParams({ jsonUrl: body.filename }),
          })
          body.expectedSha256 = current.headers.get('X-Content-SHA256') || ''
          options.body = JSON.stringify(body)
        }
      } catch (_) {
        // Let the API return the authoritative validation error.
      }
    }

    if (requestUrl.includes('/API/uploadJson.php') && options.body instanceof FormData && !options.body.has('expectedSha256')) {
      const filename = options.body.get('filename')
      if (filename) {
        const current = await nativeFetch(apiUrl('readJson.php'), {
          method: 'POST',
          body: new URLSearchParams({ jsonUrl: String(filename) }),
        })
        options.body.append('expectedSha256', current.headers.get('X-Content-SHA256') || '')
      }
    }

    options.headers = headers
    const response = await nativeFetch(input, options)
    if (response.status === 401 && !requestUrl.includes('verifyPassword.php') && !requestUrl.includes('verifyTotp.php')) {
      adminSecurity.authenticated = false
      adminSecurity.user = null
      adminSecurity.csrfToken = ''
    }
    return response
  }
}

export const checkAdminAuth = async () => {
  const response = await nativeFetch(apiUrl('authStatus.php'), { cache: 'no-store' })
  return acceptAuth(await readPayload(response))
}

export const beginAdminLogin = async (username, password) => {
  const response = await nativeFetch(apiUrl('verifyPassword.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  })
  const data = await readPayload(response)
  if (data.success) adminSecurity.challenge = data
  return data
}

export const finishAdminLogin = async (code) => {
  const response = await nativeFetch(apiUrl('verifyTotp.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  })
  return acceptAuth(await readPayload(response))
}

export const adminApi = async (endpoint, options = {}) => {
  const response = await window.fetch(apiUrl(endpoint), options)
  const data = await readPayload(response)
  if (!response.ok || data.success === false) throw new Error(data.message || `HTTP ${response.status}`)
  return data
}

export const logoutAdmin = async () => {
  if (adminSecurity.authenticated) {
    await adminApi('logout.php', { method: 'POST' }).catch(() => {})
  }
  acceptAuth({ authenticated: false })
  adminSecurity.challenge = null
  adminSecurity.recoveryCodes = []
}

export const hasAdminPermission = (permission) => {
  if (!adminSecurity.user) return false
  return Boolean(adminSecurity.user.isMaster) || (adminSecurity.user.permissions || []).includes(permission)
}
